import React from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { FieldArrayRenderProps, Field, FieldProps, useField } from 'formik';

import ContactInput from 'hellospa/components/contact-input';
import TextField from 'hellospa/foundation/control/text-field';
import ErrorMessage from 'hellospa/components/error-message';
import * as types from 'hellospa/page/prep-and-send/data/types';
import { StoreShape } from 'hellospa/redux/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { BannerErrorMessage } from 'hellospa/components/prep-and-send/formik-utils';
import { SectionHeader, SubsectionHeader } from '../header';
import TooltipUI, { ArrowDirection } from 'hellospa/foundation/control/tooltip/tooltip-ui';
import Tooltip from 'hellospa/foundation/control/tooltip';

import styles from './index.scss';
import classNames from 'classnames';
import CloseIcon from './icons/close';
import PlusIcon from './icons/plus';

const messages = defineMessages({
  addCCRecipientButton: {
    id: '',
    description: 'Add CC recipient button',
    defaultMessage: 'Add CC recipient',
  },
  fieldCCEmailLabel: {
    id: '',
    description: 'Label for CC Email',
    defaultMessage: 'Email address',
  },
  fieldCCRoleLabel: {
    id: '',
    description: 'Label for CC Email',
    defaultMessage: 'CC recipient role',
  },
  fieldEmailPlaceholder: {
    id: '',
    description: 'Placeholder for email address field',
    defaultMessage: 'email@example.com',
  },
  headerCCSection: {
    id: '',
    description: 'Header for CC Section',
    defaultMessage: 'Does anyone else need to receive a copy?',
  },
  headerMeOnlyCCSection: {
    id: '',
    description: 'Header for CC Section for Me Only request type',
    defaultMessage: 'Send via email',
  },
  removeCCRecipientTooltip: {
    id: '',
    description: 'Tooltip on Remove CC recipients button',
    defaultMessage: 'Remove CC recipient',
  },
});

type CCRoleProps = {
  index: number,
  blockCC: boolean,
};

const CCRole = ({ index, blockCC }: CCRoleProps) => {
  const intl = useIntl();
  const parent = `ccRecipients.${index}`;

  const [, ccRoleMeta] = useField<String>(`${parent}.name`);

  return (
    <div
      className={styles.fields}
      data-qa-ref={`${parent}.fields`}
    >
      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: ccRoleMeta.touched && ccRoleMeta.error,
          })}
        >
          <label htmlFor={`${parent}.name`}>
            {intl.formatMessage(messages.fieldCCRoleLabel)}
          </label>
          <Field
            name={`${parent}.name`}
          >
            {({ field }: FieldProps) => <TextField
              id={`${parent}.name`}
              disabled={blockCC}
              data-qa-ref={`${parent}.fields.name`}
              {...field} />}
          </Field>
          <div
            className={styles.errorContainer}
            role="name-error"
            data-qa-ref={`${parent}.errors.name`}
          >
            <ErrorMessage
              name={`${parent}.name`}
              className={styles.error} />
          </div>
        </div>
      </div>
    </div>
  );
};

type CCEmailProps = {
  recipient: types.CCEmail,
  index: number,
  blockCC: boolean,
};

const CCEmail = ({ recipient, index, blockCC }: CCEmailProps) => {
  const intl = useIntl();
  const parent = `ccRecipients.${index}`;
  const isEmbedded = useSelector(selectors.isEmbedded);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);

  const [, ccEmailMeta] = useField<String>(`${parent}.email`);

  return (
    <div
      className={styles.fields}
      data-qa-ref={`${parent}.fields`}
    >
      <div className={styles.fieldRow}>
        <div>
          {recipient.role && (
            <h4
              data-qa-ref={`${parent}.role`}
            >{recipient.role.name}</h4>
          )}
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: ccEmailMeta.touched && ccEmailMeta.error,
          })}
        >
          <label htmlFor={`${parent}.email`}>
            {intl.formatMessage(messages.fieldCCEmailLabel)}
          </label>
          <Field
            name={`${parent}.email`}
          >
            {({ field, form }: FieldProps) => <ContactInput
              id={`${parent}.email`}
              disabled={blockCC}
              data-qa-ref={`${parent}.fields.email`}
              placeholder={intl.formatMessage(messages.fieldEmailPlaceholder)}
              autoComplete='off'
              {...field}
              contactTypes={['E']}
              searchEnabled={!isEmbedded || isDeepIntegration}
              onSelectContact={(contact: types.Contact) => {
                form.setFieldValue(field.name, contact.value);
                form.validateField(field.name);
              }}
              onChange={(value: string) => form.setFieldValue(field.name, value)} />
            }
          </Field>
          <div
            className={styles.errorContainer}
            role="email-error"
            data-qa-ref={`${parent}.errors.email`}
          >
            <ErrorMessage
              name={`${parent}.email`}
              className={styles.error} />
          </div>
        </div>
      </div>
    </div>
  );
};

type Props = {
  recipient: types.CCEmail | types.CCRole,
  index: number,
  canDelete: boolean,
  formikHelpers: FieldArrayRenderProps,
  blockCC: boolean,
}

const CC = ({ recipient, index, canDelete, formikHelpers, blockCC }: Props) => {
  const intl = useIntl();
  const remove = () => {
    if (canDelete) {
      formikHelpers.remove(index);
    }
  };

  return (
    <div
      key={recipient.id}
      id={recipient.id}
      className={styles.recipientRow}
      data-qa-ref="ccRecipients.row"
    >
      {recipient.type === types.CCTypes.CCEmail && (
        <CCEmail
          index={index}
          recipient={recipient}
          blockCC={blockCC} />
      )}

      {recipient.type === types.CCTypes.CCRole && (
        <CCRole index={index} blockCC={blockCC} />
      )}

      <div className={styles.buttonsContainer}>
        <button
          id={recipient.id}
          type="button"
          onClick={remove}
          data-qa-ref={`ccRecipients.${index}.buttons.delete_row`}
          className={classNames({
            [styles.invisible]: !canDelete,
          })}
        >
          <CloseIcon />
          {canDelete && (
            <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
              {intl.formatMessage(messages.removeCCRecipientTooltip)}
            </TooltipUI>
          )}
        </button>
      </div>
    </div>
  );
};

type CCSectionProps = {
  formikHelpers: FieldArrayRenderProps,
}

const CCSection = (({ formikHelpers }: CCSectionProps) => {
  const intl = useIntl();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const blockCC = Boolean(useSelector(selectors.getBlockCC));
  const allowCCRecipientDelete = useSelector(selectors.allowCCRecipientDelete);
  const allowSingleCCRecipientDelete = useSelector(selectors.allowSingleCCRecipientDelete);
  const addRecipientAction = useSelector(selectors.getCCGenerator);
  const isMeOnly = useSelector(selectors.isMeOnly);

  const [recipients] = useField<(types.CC)[]>('ccRecipients');

  const allowCCRecipientAdd = useSelector((state: StoreShape) => (
    selectors.allowCCRecipientAdd(state, recipients.value)
  ));
  const header = isMeOnly ? messages.headerMeOnlyCCSection : messages.headerCCSection;
  const isSectionHeader = header === messages.headerMeOnlyCCSection;
  const canDelete = allowCCRecipientDelete && (
    allowSingleCCRecipientDelete || recipients.value.length > 1
  );

  React.useEffect(() => {
    if (showTooltip) {
      let id: NodeJS.Timeout | null = setTimeout(() => {
        setShowTooltip(false);
        id = null;
      }, 4000);
      return () => {
        if (id) {
          clearTimeout(id);
        }
      };
    }
    return () => { };
  }, [showTooltip]);

  const handleAddCC = () => {
    if (blockCC) {
      setShowTooltip((tt) => !tt);
      return;
    }
    if (allowCCRecipientAdd && addRecipientAction) {
      formikHelpers.push(addRecipientAction());
    }
  };

  if (!allowCCRecipientAdd && recipients.value.length === 0) {
    return null;
  }

  let mode: 'show-button' | 'show-warning' | 'use-tooltip' | 'hide' = 'hide';
  if (allowCCRecipientAdd && addRecipientAction) {
    mode = 'show-button';
    if (blockCC) {
      mode = 'use-tooltip';
    }
  } else if (blockCC) {
    mode = 'show-warning';
  }

  return (
    <React.Fragment>
      <BannerErrorMessage name="ccRecipients" />

      {isSectionHeader
        ? <SectionHeader text={intl.formatMessage(header)} />
        : <SubsectionHeader text={intl.formatMessage(header)} />
      }

      {recipients.value.map((recipient, index) => (
        <CC
          key={recipient.id}
          recipient={recipient}
          index={index}
          blockCC={blockCC}
          canDelete={canDelete}
          formikHelpers={formikHelpers} />
      ))}
      {mode === 'show-warning' && (
        <div className={styles.ccWarning}>
          <FormattedMessage
            id=""
            description="Warning about the user's current CC settings"
            defaultMessage="To utilize the CC feature of this template, please go to your profile settings and check the box 'When a signature request is completed - email a copy to the other signers and anyone CC'd'" />
        </div>
      )}
      {(mode === 'show-button' || mode === 'use-tooltip') && (
        <div className={styles.addRecipientContainer}>
          <Tooltip
            className={classNames(styles.ccTooltip, {
              [styles.show]: showTooltip,
            })}
            kind="small"
            content={
              <span hidden={!showTooltip}>
                <FormattedMessage
                  id=""
                  description="Warning about the user's current CC settings"
                  defaultMessage="You cannot add CCs because PDF attachments have been disabled for recipients." />
              </span>
            }
          >
            {(ref) => (
              <button
                type="button"
                ref={ref}
                onClick={handleAddCC}
                data-qa-ref="ccRecipients.buttons.add_row"
                className={styles.addRecipient}
              >
                <PlusIcon className={styles.plusIcon} />
                {intl.formatMessage(messages.addCCRecipientButton)}
              </button>
            )}
          </Tooltip>
        </div>
      )}
    </React.Fragment>
  );
});

export default CCSection;
